import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'

interface ConfirmOptions {
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
}

interface ConfirmState extends ConfirmOptions {
  message: string
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

/**
 * Promise-based replacement for window.confirm() — same call-site shape
 * (`if (!(await confirm(message))) return`) but renders a real modal
 * matching the design system instead of a native browser dialog.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState | null>(null)
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((message, options) => {
    setState({ message, ...options })
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  function handleClose(result: boolean) {
    setState(null)
    resolveRef.current?.(result)
    resolveRef.current = null
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-space-md">
          <div className="w-full max-w-sm rounded-xl bg-surface-container-lowest p-space-lg shadow-xl">
            <h2 className="mb-space-xs font-headline-sm text-headline-sm font-bold text-on-surface">
              {state.title ?? 'Confirmation'}
            </h2>
            <p className="mb-space-md font-body-md text-body-md text-on-surface-variant">{state.message}</p>
            <div className="flex items-center justify-end gap-space-xs">
              <Button type="button" variant="secondary" onClick={() => handleClose(false)}>
                {state.cancelLabel ?? 'Annuler'}
              </Button>
              <Button
                type="button"
                variant={state.destructive ? 'danger' : 'primary'}
                onClick={() => handleClose(true)}
              >
                {state.confirmLabel ?? 'Confirmer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider')
  }
  return context
}
